/**
 * The signed-in person's Microsoft token for mail (SEC-005, 2026-11).
 *
 * Mail the app sends for a person goes out with that person's OWN delegated Mail.Send: the
 * API exchanges this token On-Behalf-Of (api/src/shared/graph-mail.ts). The app's secret
 * alone can no longer send as anybody.
 *
 * MSAL asks Entra silently first (cache, then SSO in a hidden iframe on msal-redirect.html);
 * only if that fails does it open a popup — once per browser, usually closing by itself.
 * MSAL is loaded on first use so pages without mail do not pay for it.
 */
import type { PublicClientApplication } from '@azure/msal-browser';
import mailRoutes from './mail-routes.json';

/** reporting-app's registration and the Dolder tenant: public identifiers, no secret. */
const CLIENT_ID = 'c52e3e9f-1a31-4af3-bec6-128a1e0da3fa';
const TENANT_ID = '84a859fd-a5b6-4840-aa94-5ef15f9540dc';
export const MAIL_SCOPE = `api://${CLIENT_ID}/Mail.Send.AsUser`;
export const MAIL_ASSERTION_HEADER = 'X-Mail-Assertion';

interface MailRoute { method: string; route: string }

/** `/bab-report/queries/{queryId}` → /^\/bab-report\/queries\/[^/]+$/ */
function routePattern(route: string): RegExp {
    const escaped = route
        .split(/\{[^}]+\}/)
        .map(part => part.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
        .join('[^/]+');
    return new RegExp(`^${escaped}$`);
}

const PATTERNS = (mailRoutes.routes as MailRoute[]).map(r => ({
    method: r.method.toUpperCase(),
    pattern: routePattern(r.route),
}));

/** True when this request can send mail as the signed-in person. `url` is relative to /api/v1. */
export function isMailRoute(method: string | undefined, url: string): boolean {
    const m = (method ?? 'GET').toUpperCase();
    const path = url.split('?')[0];
    return PATTERNS.some(p => p.method === m && p.pattern.test(path));
}

let clientPromise: Promise<PublicClientApplication> | null = null;
let hintPromise: Promise<string | undefined> | null = null;
/** After one failed SSO attempt, go straight to the popup so it opens while the click still counts. */
let ssoFailed = false;

function client(): Promise<PublicClientApplication> {
    clientPromise ??= import('@azure/msal-browser').then(async ({ PublicClientApplication }) => {
        const pca = new PublicClientApplication({
            auth: {
                clientId: CLIENT_ID,
                authority: `https://login.microsoftonline.com/${TENANT_ID}`,
                redirectUri: `${window.location.origin}/msal-redirect.html`,
            },
            cache: { cacheLocation: 'localStorage' },
        });
        await pca.initialize();
        return pca;
    }).catch(err => {
        clientPromise = null;
        throw err;
    });
    return clientPromise;
}

/** The SWA user (UPN), so Entra picks the same account and never asks which one. */
function loginHint(): Promise<string | undefined> {
    hintPromise ??= fetch('/.auth/me')
        .then(r => (r.ok ? r.json() : null))
        .then((body: { clientPrincipal?: { userDetails?: string } } | null) =>
            body?.clientPrincipal?.userDetails?.toLowerCase() || undefined)
        .catch(() => undefined);
    return hintPromise;
}

/**
 * The token for `MAIL_SCOPE`, or null when Entra would need the person and `interactive`
 * is false, or the person closed the popup. Never throws.
 */
export async function getMailAssertion(opts: { interactive: boolean; forceRefresh?: boolean }): Promise<string | null> {
    try {
        const [pca, hint] = await Promise.all([client(), loginHint()]);
        const request = { scopes: [MAIL_SCOPE], loginHint: hint };
        const account = pca.getAllAccounts().find(a => a.username.toLowerCase() === hint) ?? null;
        if (account) {
            try {
                const r = await pca.acquireTokenSilent({ ...request, account, forceRefresh: opts.forceRefresh ?? false });
                return r.accessToken;
            } catch (err) {
                // refresh token expired or consent changed: fall through to SSO / popup
                warn('silent token', err);
            }
        }
        if (!ssoFailed || !opts.interactive) {
            try {
                return (await pca.ssoSilent(request)).accessToken;
            } catch (err) {
                ssoFailed = true;
                warn('ssoSilent', err);
            }
        }
        if (!opts.interactive) return null;
        return (await pca.acquireTokenPopup(request)).accessToken;
    } catch (err) {
        warn('popup or MSAL start', err);
        return null;
    }
}

/**
 * Loud in the console, silent on the screen: the server's 428 tells the person what to do,
 * the console tells us why. Code and message only; MSAL errors carry no token.
 */
function warn(step: string, err: unknown): void {
    const e = err as { errorCode?: string; message?: string } | null;
    console.warn(`[mail sign-in] ${step} failed:`, e?.errorCode ?? '', e?.message ?? String(err));
}

/** On app start: fill the token cache silently, so the first send needs no popup. */
export function warmMailAssertion(): void {
    void getMailAssertion({ interactive: false });
}
